/* Hooks TanStack Query do admin-push de notificações (ADR-0687). A listagem usa `useInfiniteQuery`
   (keyset cursor); os filtros entram na queryKey → refetch server-side. */
import type { CreateUserNotificationBody } from '@planuze/admin-contracts';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  type AdminNotificationsQuery,
  bulkDeleteDraftNotifications,
  createAdminNotification,
  deleteAdminNotification,
  listAdminNotifications,
  publishAdminNotification,
} from '../lib/notifications-api.js';

/* Prefixo da lista: cobre todas as combinações de filtro (a queryKey completa inclui os filtros). */
const LIST_KEY = ['admin-notifications', 'list'] as const;

type NotifPage = Awaited<ReturnType<typeof listAdminNotifications>>;
type NotifInfinite = InfiniteData<NotifPage, string | undefined>;

export function useAdminNotificationList(filters: AdminNotificationsQuery) {
  return useInfiniteQuery({
    queryKey: [...LIST_KEY, filters],
    queryFn: ({ pageParam }) => listAdminNotifications({ ...filters, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

function useInvalidate(): () => void {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: LIST_KEY });
  };
}

export function useCreateAdminNotification() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CreateUserNotificationBody) => createAdminNotification(body),
    onSuccess: invalidate,
  });
}

export function usePublishAdminNotification() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => publishAdminNotification(id),
    onSuccess: invalidate,
  });
}

/* Delete OTIMISTA (ADR-0699): remove a notificação de TODAS as páginas de TODAS as listas cacheadas
   (uma por combinação de filtro) no mesmo frame; rollback no erro, reconciliação no onSettled. */
export function useDeleteAdminNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAdminNotification(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: LIST_KEY });
      const previous = queryClient.getQueriesData<NotifInfinite>({ queryKey: LIST_KEY });
      queryClient.setQueriesData<NotifInfinite>({ queryKey: LIST_KEY }, (old) =>
        old === undefined
          ? old
          : {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                items: page.items.filter((item) => item.id !== id),
              })),
            },
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous !== undefined) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useBulkDeleteDraftNotifications() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (ids: readonly string[]) => bulkDeleteDraftNotifications(ids),
    onSuccess: invalidate,
  });
}
